/** An API call that failed, with a message a user can act on (the server's own message kept apart). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** What the server said, often technical and in English. */
    readonly detail?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** The `message` of a Backstage error body (`{ error: { message } }`), else the raw text. */
function serverMessage(text: string): string | undefined {
  try {
    const body = JSON.parse(text);
    const message = body?.error?.message ?? body?.message;
    return typeof message === 'string' ? message : undefined;
  } catch {
    return text.trim() ? text.trim().slice(0, 300) : undefined;
  }
}

/** A French message for an error status, the server's detail added where it helps. */
export function apiErrorMessage(status: number, detail?: string): string {
  if (status === 401)
    return 'Votre session a expiré : rechargez la page pour vous reconnecter.';
  if (status === 403)
    return 'Accès refusé : vous n’avez pas les droits nécessaires pour cette action.';
  if (status === 404)
    return `Élément introuvable${detail ? ` (${detail})` : ''}.`;
  if (status === 400) return `Requête refusée${detail ? ` : ${detail}` : ''}.`;
  if (status === 409)
    return detail ? `Conflit : ${detail}` : 'Conflit avec l’état actuel.';
  if (status === 503)
    return 'Service momentanément indisponible (GitHub ou Jira) : réessayez dans une minute.';
  if (status >= 500)
    return 'Erreur du portail : réessayez dans un instant ; si elle persiste, signalez-la à l’équipe plateforme.';
  return `Erreur ${status}${detail ? ` : ${detail}` : ''}.`;
}

/** The error to throw for a failed response: a French message, the server's detail kept apart. */
export async function apiError(res: Response): Promise<ApiError> {
  const text = await res.text().catch(() => '');
  const detail = serverMessage(text);
  return new ApiError(res.status, apiErrorMessage(res.status, detail), detail);
}

/** Worth retrying: the server or the network failed, not the request itself. */
export function isRetryable(error: Error): boolean {
  return !(error instanceof ApiError) || error.status >= 500;
}
